import { photoForPlacement } from "../photoPlacement.js";
import { useBandPhotos } from "./useBrandAssets.js";
import { useMediaObjectUrl } from "./useMedia.js";

/**
 * The band press photo behind the Streaming page's Spotify hero: whichever FILLED slot holds the
 * **Streaming Page** placement — explicitly assigned in Assets → Brand, or by its slot default (Alt Photo 2,
 * slot 5). The placement has existed in `app/photoPlacement.ts` since the Assets pass — as "Music hero"
 * until the page-named vocabulary landed (PO, 2026-09-06) — and Assets → Brand already assigns it.
 *
 * Deliberately a sibling of {@link useSpotlightPhoto} rather than a placement parameter on it: that hook's
 * call sites live in `features/dashboard/`, and widening its signature would touch a directory another
 * session owns. The two are five lines each and share the whole resolution rule via `photoForPlacement`,
 * so there is no logic to drift — only the placement name differs.
 *
 * Resolution goes through {@link useMediaObjectUrl}, not the payload `url` alone: `TryPublicUrl` yields NULL
 * wherever no public media host is configured (every local dev box), and the authenticated `/raw` path is the
 * app-wide degrade. Full size rather than a rendition — `sm` caps at 720px, which is soft across a hero that
 * runs the full content width.
 *
 * Returns `null` while resolving, when no photo holds the placement, and when the bytes fail — the hero then
 * keeps its gradient-only look rather than flashing a placeholder or a broken image.
 */
export function useMusicHeroPhoto(): string | null {
  const q = useBandPhotos();
  const photos = q.data?.photos;
  const chosen = photos && photoForPlacement(photos, "Streaming Page");
  const media = useMediaObjectUrl(chosen?.mediaId, chosen?.url);
  return media.url ?? null;
}
